import { ref, computed } from 'vue'
import type {
  TranscriptGroup,
  TranscriptSegment,
  LivekitContextItem,
  TimelineItem,
  Flag,
  LifecycleEvent,
  FlagDeltaPayload,
  LifecycleEventPayload,
} from '@/types/LivekitContext.ts'
import type { LivekitTimerFormatter } from './types'
import { isNotBlank } from '@/utils/String'

const formatToolDisplayName = (tool: string): string =>
  tool
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')

const toolDisplayKey = (tool: string): string => tool.replace(/[^a-z0-9]/gi, '').toLowerCase()

const normalizeFlags = (flags: (string | { value: string })[]): string[] =>
  flags.map((flag) => (typeof flag === 'object' ? flag.value : flag))

/**
 * Creates an isolated Livekit context engine instance.
 *
 * Use this for one-off or sandboxed context processing, such as export building,
 * where shared UI state must not be touched. For app-level reactive UI state,
 * use `useLivekitContext()` instead of creating a new engine manually.
 */
export const createLivekitContextEngine = (timerFormatter: LivekitTimerFormatter) => {
  const contextFetchDone = ref(false)

  const context = ref<{
    transcripts: TranscriptGroup[]
    tools: TimelineItem[]
    flags: Record<string, Flag[]>
    lifecycleEvents: LifecycleEvent[]
    callId: string | null
  }>({
    transcripts: [],
    tools: [],
    flags: {},
    lifecycleEvents: [],
    callId: null,
  })

  let firstSegmentTime: number | null = null
  const pendingToolLatencies = new Map<string, string[]>()

  const mapAlerts = (alerts: string[]) =>
    alerts.map((alert) => ({
      text: alert,
      color: 'warning',
      icon: 'warning',
    }))

  const getRelativeSeconds = (timestamp: number): number => {
    if (firstSegmentTime !== null) {
      return Math.max(0, (timestamp - firstSegmentTime) / 1000)
    }

    const callStarted = context.value.lifecycleEvents.find((event) => event.type === 'call_started')
    return callStarted ? Math.max(0, (timestamp - callStarted.timestamp) / 1000) : 0
  }

  const createContextSegment = (
    ctx: LivekitContextItem,
    channelId: number,
    isExternal: boolean,
  ): TranscriptSegment => {
    if (ctx.start_timestamp > 0) {
      if (firstSegmentTime === null || ctx.start_timestamp < firstSegmentTime) {
        firstSegmentTime = ctx.start_timestamp
      }
    }

    const validStartTime = ctx.start_timestamp > 0 ? ctx.start_timestamp : (firstSegmentTime ?? 0)
    const validEndTime = ctx.end_timestamp > 0 ? ctx.end_timestamp : validStartTime

    const start = firstSegmentTime !== null ? (validStartTime - firstSegmentTime) / 1000 : 0
    const end = firstSegmentTime !== null ? (validEndTime - firstSegmentTime) / 1000 : 0

    return {
      id: `ctx-${isNotBlank(ctx.segment_id) ? ctx.segment_id : crypto.randomUUID()}`,
      segment_id: ctx.segment_id ?? null,
      channel_id: channelId,
      is_external: isExternal,
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      text: ctx.text ?? '',
      start_time: start,
      end_time: end,
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      flags: normalizeFlags(ctx.flags ?? []),
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      tools_called: ctx.tool_results ?? [],
    }
  }

  const addContextHistory = (history: LivekitContextItem[]) => {
    if (!Array.isArray(history)) return

    for (const ctx of history) {
      const isAgent = ctx.source === 'agent'
      const channelId = isAgent ? 1 : 2
      const isExternal = !isAgent
      const segment = createContextSegment(ctx, channelId, isExternal)

      context.value.transcripts.push({
        id: `group-${crypto.randomUUID()}`,
        channel_id: channelId,
        is_external: isExternal,
        ongoing: false,
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        segmentsStatuses: mapAlerts(normalizeFlags(ctx.flags ?? [])),
        segments: [segment],
      })

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      for (const tool of ctx.tool_results ?? []) {
        const toolId = tool.id ? `tool-${tool.id}` : null
        if (toolId && context.value.tools.some((item) => item.id === toolId)) continue
        const displayTool = formatToolDisplayName(tool.name)
        const pending = pendingToolLatencies.get(toolDisplayKey(tool.name))
        const pendingDetail = pending?.shift()
        if (pending?.length === 0) pendingToolLatencies.delete(toolDisplayKey(tool.name))
        const latencyIndex = context.value.tools.findIndex(
          (item) => item.title === 'Tool latency' && item.detail === pendingDetail,
        )
        const latencyDetail =
          pendingDetail ?? (latencyIndex >= 0 ? context.value.tools[latencyIndex]?.detail : null)
        if (latencyIndex >= 0) context.value.tools.splice(latencyIndex, 1)

        context.value.tools.push({
          id: toolId ?? crypto.randomUUID(),
          time: timerFormatter.formatTimerDuration({
            value: segment.start_time,
            alwaysIncludeHours: true,
          }),
          title: 'Tool used',
          detail: latencyDetail ?? displayTool,
          type: 'tool',
        })
      }
    }
  }

  const addToolLatencyEvent = (
    tool: string,
    latencyMs: number,
    timestamp: number,
    providedLabel?: string,
  ) => {
    const displayTool = formatToolDisplayName(tool)

    let toolIndex = -1
    for (let index = context.value.tools.length - 1; index >= 0; index -= 1) {
      const item = context.value.tools[index]
      if (item.title === 'Tool used' && toolDisplayKey(item.detail) === toolDisplayKey(tool)) {
        toolIndex = index
        break
      }
    }
    const displayLabel = providedLabel ?? `${displayTool} (${(latencyMs / 1000).toFixed(1)} s)`

    if (toolIndex >= 0) {
      context.value.tools[toolIndex].detail = displayLabel
      return
    }

    const pending = pendingToolLatencies.get(toolDisplayKey(tool)) ?? []
    pending.push(displayLabel)
    pendingToolLatencies.set(toolDisplayKey(tool), pending)

    context.value.tools.push({
      id: `tool-latency-${tool}-${String(timestamp)}-${crypto.randomUUID()}`,
      time: timerFormatter.formatTimerDuration({
        value: getRelativeSeconds(timestamp),
        alwaysIncludeHours: true,
      }),
      title: 'Tool latency',
      detail: displayLabel,
      type: 'tool',
    })
  }

  const setCallId = (callId: string) => {
    context.value.callId = callId
  }

  const mergeSegment = (segment: LivekitContextItem) => {
    const existingIdx = context.value.transcripts.findIndex(
      (group) => group.segments[0]?.segment_id === segment.segment_id,
    )

    const isAgent = segment.source === 'agent'
    const channelId = isAgent ? 1 : 2
    const isExternal = !isAgent

    if (existingIdx >= 0) {
      const updated = createContextSegment(segment, channelId, isExternal)
      context.value.transcripts[existingIdx].segments[0] = updated
      context.value.transcripts[existingIdx].segmentsStatuses = mapAlerts(
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        normalizeFlags(segment.flags ?? []),
      )
      return
    }

    addContextHistory([segment])
  }

  const mergeFlagDelta = (delta: FlagDeltaPayload) => {
    const existing = context.value.flags[delta.segment_id] ?? []

    for (const flag of delta.flags) {
      const alreadyExists = existing.some(
        (item) => item.value === flag.value && item.emit_alert === flag.emit_alert,
      )

      if (!alreadyExists) {
        existing.push(flag)
      }
    }

    context.value.flags[delta.segment_id] = existing

    const transcriptIdx = context.value.transcripts.findIndex(
      (group) => group.segments[0]?.segment_id === delta.segment_id,
    )

    if (transcriptIdx >= 0) {
      const allFlags = context.value.flags[delta.segment_id] ?? []
      context.value.transcripts[transcriptIdx].segmentsStatuses = mapAlerts(
        allFlags.map((flag) => flag.value),
      )
    }
  }

  const addLifecycleEvent = (event: LifecycleEventPayload) => {
    const alreadyExists = context.value.lifecycleEvents.some(
      (item) => item.type === event.type && item.timestamp === event.timestamp,
    )

    if (alreadyExists) return

    context.value.lifecycleEvents.push(event)

    context.value.tools.push({
      id: `lifecycle-${event.type}-${String(event.timestamp)}`,
      time: timerFormatter.formatTimerDuration({
        value: getRelativeSeconds(event.timestamp),
        alwaysIncludeHours: true,
      }),
      title: event.type,
      detail: event.type,
      type: 'va',
    })
  }

  const markContextFetchDone = () => {
    contextFetchDone.value = true
  }

  const resetContext = () => {
    context.value = {
      transcripts: [],
      tools: [],
      flags: {},
      lifecycleEvents: [],
      callId: null,
    }
    firstSegmentTime = null
    pendingToolLatencies.clear()
    contextFetchDone.value = false
  }

  return {
    allTranscripts: computed(() => context.value.transcripts),
    transcripts: computed(() =>
      context.value.transcripts.filter((group) => group.segments.some((segment) => segment.text)),
    ),
    tools: computed(() => [...context.value.tools].sort((a, b) => a.time.localeCompare(b.time))),
    flags: computed(() => context.value.flags),
    lifecycleEvents: computed(() => context.value.lifecycleEvents),
    callId: computed(() => context.value.callId),
    contextFetchDone,
    getRelativeSeconds,
    addContextHistory,
    addToolLatencyEvent,
    mergeSegment,
    mergeFlagDelta,
    addLifecycleEvent,
    setCallId,
    markContextFetchDone,
    resetContext,
  }
}
